import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('af_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('af_token');
      window.location.href = '/panel/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Auth ─────────────────────────────────────────────────────
export const authAPI = {
  login:    (data)  => api.post('/auth/login', data),
  register: (data)  => api.post('/auth/register', data),
  logout:   ()      => api.post('/auth/logout'),
  me:       ()      => api.get('/auth/me'),
};

// ── Dashboard ────────────────────────────────────────────────
export const dashboardAPI = {
  index: () => api.get('/dashboard'),
};

// ── Customers ────────────────────────────────────────────────
export const customersAPI = {
  list:   (p) => api.get('/customers', { params: p }),
  create: (d) => api.post('/customers', d),
  show:   (id)=> api.get(`/customers/${id}`),
  update: (id,d) => api.put(`/customers/${id}`, d),
  delete: (id)=> api.delete(`/customers/${id}`),
};

// ── Vehicles ─────────────────────────────────────────────────
export const vehiclesAPI = {
  list:        (p)    => api.get('/vehicles', { params: p }),
  create:      (d)    => api.post('/vehicles', d),
  show:        (id)   => api.get(`/vehicles/${id}`),
  update:      (id,d) => api.put(`/vehicles/${id}`, d),
  delete:      (id)   => api.delete(`/vehicles/${id}`),
  byCustomer:  (cid)  => api.get(`/customers/${cid}/vehicles`),
};

// ── Services ─────────────────────────────────────────────────
export const servicesAPI = {
  list:       ()     => api.get('/services'),
  create:     (d)    => api.post('/services', d),
  show:       (id)   => api.get(`/services/${id}`),
  update:     (id,d) => api.put(`/services/${id}`, d),
  delete:     (id)   => api.delete(`/services/${id}`),
  savePrices: (id,d) => api.post(`/services/${id}/prices`, d),
};

// ── Packages ─────────────────────────────────────────────────
export const packagesAPI = {
  list:   ()     => api.get('/packages'),
  create: (d)    => api.post('/packages', d),
  show:   (id)   => api.get(`/packages/${id}`),
  update: (id,d) => api.put(`/packages/${id}`, d),
  delete: (id)   => api.delete(`/packages/${id}`),
};

// ── Appointments ─────────────────────────────────────────────
export const appointmentsAPI = {
  list:          (p)    => api.get('/appointments', { params: p }),
  create:        (d)    => api.post('/appointments', d),
  show:          (id)   => api.get(`/appointments/${id}`),
  update:        (id,d) => api.put(`/appointments/${id}`, d),
  updateStatus:  (id,d) => api.patch(`/appointments/${id}/status`, d),
  delete:        (id)   => api.delete(`/appointments/${id}`),
  availableSlots:(p)    => api.get('/appointments/slots', { params: p }),
  slots:         (p)    => api.get('/appointments/slots', { params: p }),
  convertToOrder:(id)   => api.post(`/appointments/${id}/to-order`),
};

// ── Checklist ────────────────────────────────────────────────
export const checklistAPI = {
  show:        (orderId)   => api.get(`/orders/${orderId}/checklist`),
  save:        (orderId,d) => api.post(`/orders/${orderId}/checklist`, d),
  uploadPhoto: (orderId,f) => api.post(`/orders/${orderId}/checklist/photos`, f, { headers:{'Content-Type':'multipart/form-data'} }),
};

// ── Orders ───────────────────────────────────────────────────
export const ordersAPI = {
  list:         (p)    => api.get('/orders', { params: p }),
  create:       (d)    => api.post('/orders', d),
  show:         (id)   => api.get(`/orders/${id}`),
  update:       (id,d) => api.put(`/orders/${id}`, d),
  updateStatus: (id,d) => api.patch(`/orders/${id}/status`, d),
  delete:       (id)   => api.delete(`/orders/${id}`),
  uploadPhoto:  (id,f) => {
    const fd = new FormData(); fd.append('photo', f);
    return api.post(`/orders/${id}/photos`, fd, { headers: {'Content-Type':'multipart/form-data'} });
  },
  receiptLink:  (id)  => api.get(`/orders/${id}/receipt`),
};

// ── Financial ────────────────────────────────────────────────
export const financialAPI = {
  accounts:          (p)    => api.get('/financial/accounts', { params: p }),
  createAccount:     (d)    => api.post('/financial/accounts', d),
  transactions:      (p)    => api.get('/financial/transactions', { params: p }),
  createTransaction: (d)    => api.post('/financial/transactions', d),
  updateTransaction: (id,d) => api.put(`/financial/transactions/${id}`, d),
  markPaid:          (id,d) => api.patch(`/financial/transactions/${id}/pay`, d),
  deleteTransaction: (id)   => api.delete(`/financial/transactions/${id}`),
  summary:           (p)    => api.get('/financial/summary', { params: p }),
  cashflow:          (p)    => api.get('/financial/cashflow', { params: p }),
  exportUrl:         (p)    => {
    const token = localStorage.getItem('af_token');
    const qs = new URLSearchParams({ ...p, token }).toString();
    return `${API_BASE}/financial/export?${qs}`;
  },
};

// ── Products / Stock ─────────────────────────────────────────
export const productsAPI = {
  list:     (p)    => api.get('/products', { params: p }),
  create:   (d)    => api.post('/products', d),
  show:     (id)   => api.get(`/products/${id}`),
  update:   (id,d) => api.put(`/products/${id}`, d),
  delete:   (id)   => api.delete(`/products/${id}`),
  movement: (id,d) => api.post(`/products/${id}/movement`, d),
  lowStock: ()     => api.get('/products/low-stock'),
};

// ── Commissions ──────────────────────────────────────────────
export const commissionAPI = {
  list:     (p)  => api.get('/commissions', { params: p }),
  markPaid: (id) => api.patch(`/commissions/${id}/pay`),
};

// ── Employees ────────────────────────────────────────────────
export const employeesAPI = {
  list:   ()     => api.get('/employees'),
  create: (d)    => api.post('/employees', d),
  show:   (id)   => api.get(`/employees/${id}`),
  update: (id,d) => api.put(`/employees/${id}`, d),
  delete: (id)   => api.delete(`/employees/${id}`),
};

// ── Reports ──────────────────────────────────────────────────
export const reportsAPI = {
  sales:     (p) => api.get('/reports/sales', { params: p }),
  services:  (p) => api.get('/reports/services', { params: p }),
  employees: (p) => api.get('/reports/employees', { params: p }),
  customers: (p) => api.get('/reports/customers', { params: p }),
};

// ── Landing ──────────────────────────────────────────────────
export const landingAPI = {
  show:         ()     => api.get('/landing'),
  update:       (d)    => api.put('/landing', d),
  slides:        ()     => api.get('/landing/slides'),
  addSlide:      (fd)   => api.post('/landing/slides', fd, { headers:{'Content-Type':'multipart/form-data'} }),
  createSlide:   (d)    => api.post('/landing/slides', d),
  updateSlide:   (id,d) => api.put(`/landing/slides/${id}`, d),
  deleteSlide:   (id)   => api.delete(`/landing/slides/${id}`),
  gallery:       ()     => api.get('/landing/gallery'),
  uploadGallery: (fd)   => api.post('/landing/gallery', fd, { headers:{'Content-Type':'multipart/form-data'} }),
  deleteGallery: (id)   => api.delete(`/landing/gallery/${id}`),
  reviews:       ()     => api.get('/landing/reviews'),
  createReview:  (d)    => api.post('/landing/reviews', d),
  updateReview:  (id,d) => api.put(`/landing/reviews/${id}`, d),
  deleteReview:  (id)   => api.delete(`/landing/reviews/${id}`),
  publish:       ()     => api.post('/landing/publish'),
};

// ── Tenant (própria conta) ───────────────────────────────────
export const tenantAPI = {
  show:       ()   => api.get('/tenant'),
  update:     (d)  => api.put('/tenant', d),
  uploadLogo: (fd) => api.post('/tenant/logo', fd, { headers:{'Content-Type':'multipart/form-data'} }),
};

// ── Users ────────────────────────────────────────────────────
export const usersAPI = {
  list:   ()     => api.get('/users'),
  create: (d)    => api.post('/users', d),
  update: (id,d) => api.put(`/users/${id}`, d),
  delete: (id)   => api.delete(`/users/${id}`),
};

// ── Plans (public) ───────────────────────────────────────────
export const plansAPI = {
  list: () => api.get('/plans'),
};

// ── Subscriptions ────────────────────────────────────────────
export const subscriptionsAPI = {
  create:          (d) => api.post('/subscriptions', d),
  status:          ()  => api.get('/subscriptions/status'),
  cancel:          ()  => api.post('/subscriptions/cancel'),
  validateCoupon:  (d) => api.post('/coupons/validate', d),
};

// ── Admin ────────────────────────────────────────────────────
export const adminAPI = {
  tenants:      (p)    => api.get('/admin/tenants', { params: p }),
  tenantShow:   (id)   => api.get(`/admin/tenants/${id}`),
  tenantCreate: (d)    => api.post('/admin/tenants', d),
  tenantUpdate: (id,d) => api.put(`/admin/tenants/${id}`, d),
  tenantBlock:  (id)   => api.patch(`/admin/tenants/${id}/block`),
  tenantUnblock:(id)   => api.patch(`/admin/tenants/${id}/unblock`),
  tenantCancel: (id)   => api.patch(`/admin/tenants/${id}/cancel`),
  metrics:      ()     => api.get('/admin/metrics'),
  plans:        ()     => api.get('/admin/plans'),
  planCreate:   (d)    => api.post('/admin/plans', d),
  planUpdate:   (id,d) => api.put(`/admin/plans/${id}`, d),
  coupons:      ()     => api.get('/admin/coupons'),
  couponCreate: (d)    => api.post('/admin/coupons', d),
  couponDelete: (id)   => api.delete(`/admin/coupons/${id}`),
  logs:           (p)  => api.get('/admin/logs', { params: p }),
  cronRun:        ()   => api.post('/admin/cron/run'),
  settings:       ()   => api.get('/admin/settings'),
  updateSettings: (d)  => api.put('/admin/settings', d),
  testEmail:      (d)  => api.post('/admin/settings/test-email', d),
};
